import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ToastVariants } from '@dotcom/crown-ui';
import { setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { loadProjectDetails } from '../../../utils/digital-greetings/load-project-details';

type UseAnimationPreviewOptions = {
  projectId: string;
  autoPlay?: boolean;
  onPlay?: () => void;
};

export const useAnimationPreview = ({ projectId, autoPlay = false, onPlay }: UseAnimationPreviewOptions) => {
  const [isAnimationDialogOpen, setIsAnimationDialogOpen] = useState(false);
  const [animationPreviewImages, setAnimationPreviewImages] = useState<string[]>([]);
  const autoPlayedProjectId = useRef<string>();
  const pendingProjects = useRef(new Set<string>());
  const { appDispatch } = useAppContext();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    mutate: loadAnimation,
    isPending: isLoading,
    isError,
  } = useMutation({
    retry: false,
    mutationFn: async (requestedProjectId: string) => {
      const animationPanels = await loadProjectDetails(requestedProjectId, queryClient);
      const frontImage = animationPanels.F?.resize_urls.M;
      const insideImage = animationPanels.I?.resize_urls.M;

      if (!frontImage || !insideImage) {
        throw new Error('Required animation panels were not found');
      }

      return [frontImage, insideImage];
    },
    onSuccess: (images) => {
      setAnimationPreviewImages(images);
      setIsAnimationDialogOpen(true);
      onPlay?.();
    },
    onError: () => {
      setIsToasterOpen(appDispatch, {
        title: t('previewView.animationPreview.unavailableToastTitle'),
        children: t('previewView.animationPreview.unavailableToastDescription'),
        variant: ToastVariants.Warning,
      });
    },
    onSettled: (_data, _error, requestedProjectId) => {
      pendingProjects.current.delete(requestedProjectId);
    },
  });
  const playAnimation = useCallback(() => {
    if (!projectId || pendingProjects.current.has(projectId)) return;
    // Block duplicate clicks before the mutation's pending state renders.
    pendingProjects.current.add(projectId);
    loadAnimation(projectId);
  }, [loadAnimation, projectId]);

  const closeAnimation = useCallback(() => {
    setIsAnimationDialogOpen(false);
  }, []);

  useEffect(() => {
    if (autoPlay && projectId && autoPlayedProjectId.current !== projectId) {
      autoPlayedProjectId.current = projectId;
      void playAnimation();
    }
  }, [autoPlay, playAnimation, projectId]);

  return {
    animationPreviewImages,
    closeAnimation,
    isAnimationDialogOpen,
    playAnimation,
    isLoading,
    isError,
  };
};
